import React from 'react'
import { Link } from 'react-router-dom'
import { useTheme } from '../hooks/use-theme'

interface LogoProps {
  size?: 'sm' | 'md' | 'lg'
  className?: string
  /** When set, logo wordmark links home. */
  to?: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
  /** Force light-surface colors (marketing / cream backgrounds). */
  forceLight?: boolean
  /** Show “Developed by breakroomcup.com” under the wordmark (nav). */
  showCredit?: boolean
}

const sizeStyles = {
  sm: { text: 'text-[0.95rem] sm:text-xl', hat: 'h-4 w-4 sm:h-5 sm:w-5', gap: 'gap-0.5 sm:gap-1' },
  md: { text: 'text-xl sm:text-2xl', hat: 'h-6 w-6 sm:h-7 sm:w-7', gap: 'gap-1.5' },
  lg: { text: 'text-2xl sm:text-3xl md:text-4xl', hat: 'h-8 w-8 sm:h-9 sm:w-9', gap: 'gap-2' },
} as const

/**
 * Academ One wordmark — matches flutter-academ: academ (teal) + one (terracotta) + hat.
 */
const Logo: React.FC<LogoProps> = ({
  size = 'md',
  className = '',
  to,
  onClick,
  forceLight,
  showCredit = false,
}) => {
  const { mode } = useTheme()
  const onLight = forceLight || mode !== 'dark'
  const s = sizeStyles[size]
  const academColor = onLight ? 'text-[#0B3333]' : 'text-white'
  const oneColor = 'text-[#9A4538]'
  const creditColor = onLight ? 'text-[#0B3333]/80' : 'text-white/85'

  const mark = (
    <span
      className={`inline-flex items-center ${s.gap} font-extrabold tracking-[-0.03em] leading-none select-none`}
      aria-label="Academ One"
    >
      <span className={`${s.text} ${academColor}`}>academ</span>
      <span className={`${s.text} ${oneColor}`}>one</span>
      <img
        src={`${process.env.PUBLIC_URL || ''}/images/academone-hat.png`}
        alt=""
        className={`${s.hat} object-contain shrink-0`}
        onError={(e) => {
          ;(e.target as HTMLImageElement).style.display = 'none'
        }}
      />
    </span>
  )

  const wordmark =
    to !== undefined ? (
      <Link
        to={to}
        onClick={onClick}
        className="inline-flex items-center bg-transparent"
        aria-label="Academ One home"
      >
        {mark}
      </Link>
    ) : (
      mark
    )

  return (
    <span className={`inline-flex flex-col items-start gap-0.5 shrink-0 ${className}`.trim()}>
      {wordmark}
      {showCredit && (
        <a
          href="https://breakroomcup.com"
          target="_blank"
          rel="noopener noreferrer"
          className={`text-[10px] sm:text-xs font-medium leading-tight ${creditColor} hover:text-[#9A4538] transition-colors`}
        >
          Developed by breakroomcup.com
        </a>
      )}
    </span>
  )
}

export default Logo
